import Navbar from "./components/Navbar";

import { lazy, Suspense, useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "./store/useAuthStore";
import { useThemeStore } from "./store/useThemeStore";
import { useWalletStore } from "./store/useWalletStore";

import { Loader } from "lucide-react";
import { Toaster } from "react-hot-toast";

const HomePage = lazy(() => import("./pages/HomePage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const DiscoveryPage = lazy(() => import("./pages/DiscoveryPage"));
const MatchesPage = lazy(() => import("./pages/MatchesPage"));
const ProfileCompletionPage = lazy(() => import("./pages/ProfileCompletionPage"));
const WatchPartyPage = lazy(() => import("./pages/WatchPartyPage"));
const CouplesDashboard = lazy(() => import("./pages/CouplesDashboard"));
const LinkPartnerPage = lazy(() => import("./pages/LinkPartnerPage"));
const MemoriesPage = lazy(() => import("./pages/MemoriesPage"));
const ImportantDatesPage = lazy(() => import("./pages/ImportantDatesPage"));
const CouplesChatPage = lazy(() => import("./pages/CouplesChatPage"));
const WaitlistPage = lazy(() => import("./pages/WaitlistPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PulseMapPage = lazy(() => import("./pages/PulseMapPage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const EditProfilePage = lazy(() => import("./pages/EditProfilePage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const PartyLoungePage = lazy(() => import("./pages/PartyLoungePage"));

const CasinoLobbyPage = lazy(() => import("./pages/casino/CasinoLobbyPage"));
const BlackjackPage = lazy(() => import("./pages/casino/BlackjackPage"));
const RoulettePage = lazy(() => import("./pages/casino/RoulettePage"));
const HiLoPage = lazy(() => import("./pages/casino/HiLoPage"));
const VideoPokerPage = lazy(() => import("./pages/casino/VideoPokerPage"));
const DragonTowerPage = lazy(() => import("./pages/casino/DragonTowerPage"));
const BaccaratPage = lazy(() => import("./pages/casino/BaccaratPage"));

import { setGlobalNavigate } from "./lib/navigation";
import { collectAndSendTelemetry } from "./lib/telemetry";

import React from "react";

class ChunkErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Dynamic import error caught by boundary:", error);
    const chunkFailedMessage = /loading chunk|failed to fetch|dynamically imported/i;
    if (error && chunkFailedMessage.test(error.message || "")) {
      const storageKey = 'last_chunk_reload';
      const lastReload = sessionStorage.getItem(storageKey);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem(storageKey, now.toString());
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-base-100 p-4 text-center">
          <Loader className="size-8 text-primary animate-spin mb-3" />
          <p className="text-sm font-bold text-base-content/70 mb-2">Updating application...</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn btn-sm btn-primary rounded-full mt-2"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => {
  const { authUser, checkAuth, isCheckingAuth, syncDeviceFingerprint } = useAuthStore();
  const { theme } = useThemeStore();
  const location = useLocation();
  const navigate = useNavigate();
  const fingerprintSynced = useRef(false);

  useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (authUser && !isCheckingAuth) {
      if (!fingerprintSynced.current) {
        syncDeviceFingerprint();
        fingerprintSynced.current = true;
      }
      
      // Subscribe to wallet events
      useWalletStore.getState().subscribeToWalletEvents();
      
      // Fix #23: Use a separate ref so telemetry fires only once per session,
      // not on every authUser state change (e.g., profile updates)
    }

    return () => {
      useWalletStore.getState().unsubscribeFromWalletEvents();
    };
  }, [authUser, isCheckingAuth, syncDeviceFingerprint]);

  // Telemetry — runs once after first successful auth, never again
  const telemetrySent = useRef(false);
  useEffect(() => {
    if (authUser && !isCheckingAuth && !telemetrySent.current) {
      telemetrySent.current = true;
      collectAndSendTelemetry();
    }
  }, [authUser, isCheckingAuth]);

  if (isCheckingAuth) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="size-10 animate-spin" />
      </div>
    );
  }

  // Redirect to profile completion if profile is strictly incomplete
  if (authUser && authUser.isProfileComplete === false && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  // Redirect to waitlist lounge is removed as waitlist is disabled

  return (
    <div data-theme={theme}>
      <Navbar />

      <ChunkErrorBoundary>
        <Suspense fallback={
          <div className="flex items-center justify-center h-screen bg-base-300">
            <div className="flex flex-col items-center gap-4">
              <Loader className="size-10 text-primary animate-spin" />
              <p className="text-sm font-bold text-base-content/60 animate-pulse">Loading...</p>
            </div>
          </div>
        }>
          <Routes>
            <Route path="/" element={authUser ? (authUser.status === "coupled" ? <Navigate to="/our-space" /> : <HomePage />) : <Navigate to="/login" />} />
            <Route path="/signup" element={!authUser ? <SignUpPage /> : <Navigate to="/" />} />
            <Route path="/login" element={!authUser ? <LoginPage /> : <Navigate to="/" />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/profile" element={authUser ? <ProfilePage /> : <Navigate to="/login" />} />
            <Route path="/discover" element={authUser && authUser.status !== "coupled" ? <DiscoveryPage /> : <Navigate to={authUser ? "/our-space" : "/login"} />} />
            <Route path="/matches" element={authUser && authUser.status !== "coupled" ? <MatchesPage /> : <Navigate to={authUser ? "/our-space" : "/login"} />} />
            <Route path="/watch-party" element={authUser ? <WatchPartyPage /> : <Navigate to="/login" />} />
            <Route path="/our-space" element={authUser ? <CouplesDashboard /> : <Navigate to="/login" />} />
            <Route path="/link-partner" element={authUser ? <LinkPartnerPage /> : <Navigate to="/login" />} />
            <Route path="/our-chat" element={authUser && authUser.status === "coupled" ? <CouplesChatPage /> : <Navigate to="/login" />} />
            <Route path="/memories" element={authUser && authUser.status === "coupled" ? <MemoriesPage /> : <Navigate to="/login" />} />
            <Route path="/dates" element={authUser && authUser.status === "coupled" ? <ImportantDatesPage /> : <Navigate to="/login" />} />
            <Route
              path="/complete-profile"
              element={
                authUser
                  ? <ProfileCompletionPage />
                  : <Navigate to="/login" />
              }
            />
            <Route path="/waitlist" element={authUser && authUser.status === "waitlisted" ? <WaitlistPage /> : <Navigate to="/" />} />
            <Route path="/admin" element={authUser?.isAdmin ? <AdminPage /> : <Navigate to="/" />} />
            <Route path="/map" element={authUser ? <PulseMapPage /> : <Navigate to="/login" />} />
            <Route path="/leaderboard" element={authUser ? <LeaderboardPage /> : <Navigate to="/login" />} />
            <Route path="/edit-profile" element={authUser ? <EditProfilePage /> : <Navigate to="/login" />} />
            <Route path="/wallet" element={authUser ? <WalletPage /> : <Navigate to="/login" />} />
            <Route path="/party" element={authUser ? <PartyLoungePage /> : <Navigate to="/login" />} />
            
            {/* Casino Routes */}
            <Route path="/casino" element={authUser ? <CasinoLobbyPage /> : <Navigate to="/login" />} />
            <Route path="/casino/blackjack" element={authUser ? <BlackjackPage /> : <Navigate to="/login" />} />
            <Route path="/casino/roulette" element={authUser ? <RoulettePage /> : <Navigate to="/login" />} />
            <Route path="/casino/hilo" element={authUser ? <HiLoPage /> : <Navigate to="/login" />} />
            <Route path="/casino/videopoker" element={authUser ? <VideoPokerPage /> : <Navigate to="/login" />} />
            <Route path="/casino/dragontower" element={authUser ? <DragonTowerPage /> : <Navigate to="/login" />} />
            <Route path="/casino/baccarat" element={authUser ? <BaccaratPage /> : <Navigate to="/login" />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>

      <Toaster />
    </div>
  );
};
export default App;
